import React from 'react';
import type { ToggleIcons } from 'react-toggle';
import ReactToggle from 'react-toggle';
import cx from 'classnames';
import { FieldRenderProps } from 'react-final-form';
import styles from './ToggleV2.module.less';

export enum LabelSideVariant {
  LEFT = 'left',
  RIGHT = 'right',
  TOP = 'top',
}

export interface ToggleV2Props extends Partial<FieldRenderProps<boolean, HTMLElement>> {
  label?: string | React.ReactNode;
  /** Orientation of label with respect to the toggle */
  labelSide?: LabelSideVariant;
  id: string;
  className?: string;
  inline?: boolean;
  disabled?: boolean;
  /** Show icons for toggle state */
  withIcons?: boolean;
  /** For accessibility, use this prop when no labels exist for the input */
  ariaLabel?: string;
  /** For standalone controlled input not part of form */
  value?: boolean;
  /** For standalone controlled input not part of form */
  onChange?: (e?: React.ChangeEvent<Element>) => void;
  /** For showing red destructive state when value `false` */
  destructive?: boolean;
  /** For showing form label styling; bold */
  formLabelStyling?: boolean;
  /** Passthrough props passed to <input /> */
  [x: string]: unknown;
}

/**
 * Wrapper for component for `react-toggle` that adds design-system specific features like styling and label placement.
 * Toggle is compatible with react-final-form (see example [here](?path=/story/components-form-example--example) and without.
 * If you&apos;re using the toggle component without react-form you&apos;ll need to control the value of the toggle.
 */
const ToggleV2: React.FC<ToggleV2Props> = ({
  input,
  label,
  labelSide = LabelSideVariant.LEFT,
  id,
  className,
  inline = true,
  disabled,
  withIcons,
  meta,
  ariaLabel,
  value,
  onChange,
  destructive,
  formLabelStyling,
  ...rest
}) => {
  const inputValue = input ? input.value : value;
  const handleChange: React.ChangeEventHandler = (e) => {
    if (input) {
      input.onChange(!inputValue);
    } else if (onChange) {
      onChange(e);
    }
  };
  const hasError = !!(meta?.touched && meta?.error);
  const mainClasses = cx(styles.container, className, {
    [styles.inlineContainer]: inline,
  });
  const toggleWrapperClasses = cx(styles.toggleWrapper, {
    [styles.labelInline]:
      labelSide === LabelSideVariant.LEFT || labelSide === LabelSideVariant.RIGHT,
    [styles.destructive]: destructive,
  });
  const labelClasses = cx(styles.label, {
    [styles.labelRight]: labelSide === LabelSideVariant.RIGHT,
    [styles.labelLeft]: labelSide === LabelSideVariant.LEFT,
    [styles.labelBold]: formLabelStyling,
  });
  const icons: ToggleIcons = {
    checked: (
      <span className={styles.icon}>
        <i className="zola-ui-icon zola-ui-icon-checkmark" />
      </span>
    ),
    unchecked: (
      <span className={styles.icon}>
        <i className="zola-ui-icon zola-ui-icon-close" />
      </span>
    ),
  };
  return (
    <div className={mainClasses}>
      {label && (labelSide === LabelSideVariant.LEFT || labelSide === LabelSideVariant.TOP) ? (
        <label htmlFor={id} className={labelClasses}>
          {label}
        </label>
      ) : null}
      <div className={toggleWrapperClasses}>
        <ReactToggle
          id={id}
          checked={inputValue}
          disabled={disabled}
          onChange={handleChange}
          icons={withIcons ? icons : false}
          aria-label={ariaLabel}
          {...rest}
        />
      </div>
      {label && labelSide === LabelSideVariant.RIGHT ? (
        <label htmlFor={id} className={labelClasses}>
          {label}
        </label>
      ) : null}
      {hasError && <span className={styles.helperText}>{meta?.error}</span>}
    </div>
  );
};

export default ToggleV2;
